import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AccountDetailScreen } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/account-detail-screen';
import { AccountListScreen } from '@/experiences/sales-portal/domain/accounts/screens/account-list/account-list-screen';
import {
  accountsTabScreens,
  directTabScreens,
  insightsTabScreens,
  orderEntryTabScreens,
  orderGuidesTabScreens,
  pricingTabScreens,
  prospectsTabScreens,
} from '@/experiences/sales-portal/sales-portal-route-config';
import { AlertContainer, ToastProvider } from '@usepepper/sprout-kit-ui';
import {
  BottomTabBarProps,
  createBottomTabNavigator,
} from '@react-navigation/bottom-tabs';
import {
  CardStyleInterpolators,
  createStackNavigator,
  StackNavigationOptions,
} from '@react-navigation/stack';
import { CopilotChatScreen } from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/copilot-chat-screen';
import {
  DefaultTheme,
  getPathFromState,
  getStateFromPath,
  LinkingOptions,
  NavigationContainer,
  NavigationState,
} from '@react-navigation/native';
import { EmployeeListScreen } from '@/experiences/sales-portal/domain/accounts/screens/employee-list';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { getActiveRoute, isWeb } from '@/utils/navigation';
import { InsightsScreenWrapper } from '@/experiences/sales-portal/domain/insights/screens/insights-screen-wrapper';
import { IntercomRouteSync } from '@/experiences/sales-portal/contexts/intercom-route-sync';
import { KeyboardShortcutsProvider } from '@/contexts/keyboard-shortcuts/keyboard-shortcuts-context';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { Linking, Platform } from 'react-native';
import { LogoLoadingIndicator } from '@/components/logo-loading-indicator';
import { MicroProductLoaderScreen } from '@/experiences/sales-portal/domain/microproducts/screens/loader/microproducts-loader-screen';
import {
  NavigationGuardStatus,
  NavigationStackGuard,
} from '@/components/navigation-stack-guard';
import { OrderEntryScreenWrapper } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/order-entry-screen-wrapper';
import { OrderGuidesListScreen } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/order-guides-list-screen';
import { PriceQuotesScreen } from '@/experiences/sales-portal/domain/pricing/screens/quotes/price-quotes-screen';
import { PricingAgentReviewScreenWrapper } from '@/experiences/sales-portal/domain/pricing-agent/screens/pricing-agent-review-screen-wrapper';
import { PricingAgentTasksScreenWrapper } from '@/experiences/sales-portal/domain/pricing-agent/screens/pricing-agent-tasks-screen-wrapper';
import { PricingBookmarksScreenWrapper } from '@/experiences/sales-portal/domain/pricing/screens/bookmarks/pricing-bookmarks-screen-wrapper';
import { PricingCustomerDetailScreenWrapper } from '@/experiences/sales-portal/domain/pricing/screens/customers/pricing-customer-detail-screen-wrapper';
import { PricingCustomersScreenWrapper } from '@/experiences/sales-portal/domain/pricing/screens/customers/pricing-customers-screen-wrapper';
import { PricingItemManagementScreenWrapper } from '@/experiences/sales-portal/domain/pricing/screens/management/pricing-item-management-screen-wrapper';
import { PricingManagementReviewScreenWrapper } from '@sales-portal/domain/pricing/screens/management/pricing-management-review-screen-wrapper';
import { PricingManagementScreenWrapper } from '@/experiences/sales-portal/domain/pricing/screens/management/pricing-management-screen-wrapper';
import { PricingMicroProductScreenWrapper } from '@/experiences/sales-portal/domain/pricing/screens/pricing-micro-product-screen-wrapper';
import { PricingOverviewScreenWrapper } from '@/experiences/sales-portal/domain/pricing/screens/overview/pricing-overview-screen-wrapper';
import { ProspectDetailScreen } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/prospect-detail-screen';
import { ProspectFinderScreen } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/prospect-finder-screen';
import { ProspectsScreen } from '@/experiences/sales-portal/domain/prospects/screens/prospects/prospects-screen';
import {
  SALES_PORTAL_ROUTES,
  SALES_PORTAL_TAB_ROUTES,
} from '@/navigators/routes';
import {
  SalesPortalAccountsStackParams,
  SalesPortalInsightsStackParams,
  SalesPortalOrderEntryParams,
  SalesPortalOrderEntryStackParams,
  SalesPortalOrderGuideStackParams,
  SalesPortalPricingStackParams,
  SalesPortalProspectsStackParams,
  SalesPortalTabParams,
} from '@/navigators/types';
import { SalesPortalHomeScreen } from '@/experiences/sales-portal/screens/sales-portal-home-screen';
import { SalesPortalIntercomProvider } from '@/experiences/sales-portal/contexts/intercom-context';
import { SalesPortalKeyboardScope } from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SalesPortalNotFoundScreen } from '@/experiences/sales-portal/screens/sales-portal-not-found-screen';
import { SalesPortalTabBar } from '@/experiences/sales-portal/containers/sales-portal-tab-bar/sales-portal-tab-bar';
import { SuperintelligenceScreen } from '@/experiences/sales-portal/domain/superintelligence/screens/superintelligence-screen';
import {
  toLandingRoute,
  useSalesPortalNavigationStore,
} from '@/experiences/sales-portal/blocs/stores/sales-portal-navigation-store';
import { useAppMinimumVersion } from '@/hooks/use-app-minimum-version';
import { useDefaultStackScreenOptions } from '@/hooks/use-default-stack-screen-options';
import { useNavigationGuardStatus } from '@/hooks/use-navigation-guard-status';
import {
  USER_SETTINGS_KEY,
  UserSettingsAdapter,
} from '@/adapters/user-settings';
import { useSalesPortalFennelInit } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-fennel-init';
import { useTheme } from '@/contexts/theme-context';

const Tab = createBottomTabNavigator<SalesPortalTabParams>();

// Module-level so the navigator doesn't see a new tabBar identity every render
const renderSalesPortalTabBar = (props: BottomTabBarProps) => (
  <SalesPortalTabBar {...props} />
);
const AccountsStackNav = createStackNavigator<SalesPortalAccountsStackParams>();
const InsightsStackNav = createStackNavigator<SalesPortalInsightsStackParams>();
const OrderEntryStackNav =
  createStackNavigator<SalesPortalOrderEntryStackParams>();
const OrderGuideStackNav =
  createStackNavigator<SalesPortalOrderGuideStackParams>();
const PricingStackNav = createStackNavigator<SalesPortalPricingStackParams>();
const ProspectsStackNav =
  createStackNavigator<SalesPortalProspectsStackParams>();

interface SalesPortalUserSettings {
  selectedRestaurantUUID: string;
  selectedSupplierUUID: string;
}

enum UserSettingsStatus {
  LOADING = 'loading',
  NOT_FOUND = 'not_found',
}

// Used for initial URL parsing to extract supplier/restaurant UUIDs
// before the full NavigationContainer mounts. Must include every
// deep-linkable route that carries those UUIDs.
const initialUrlLinkingConfig: Parameters<typeof getStateFromPath>[1] = {
  screens: {
    [SALES_PORTAL_TAB_ROUTES.OrderEntry]: {
      screens: {
        [SALES_PORTAL_ROUTES.OrderEntry]:
          orderEntryTabScreens[SALES_PORTAL_ROUTES.OrderEntry],
      },
    },
    [SALES_PORTAL_TAB_ROUTES.OrderGuides]: {
      screens: {
        [SALES_PORTAL_ROUTES.OrderGuides]:
          orderGuidesTabScreens[SALES_PORTAL_ROUTES.OrderGuides],
      },
    },
  },
};

const STRIPPED_QUERY_PARAMS = ['lastCompletedAt', 'supplierUUID', 'taskName'];

const stripInternalQueryParams = (path: string): string => {
  const [base, query] = path.split('?');
  if (!query) {
    return path;
  }
  const kept = query
    .split('&')
    .filter(
      (part) =>
        !STRIPPED_QUERY_PARAMS.some((name) => part.startsWith(`${name}=`)),
    );
  return kept.length ? `${base}?${kept.join('&')}` : base;
};

const salesPortalGetPathFromState: typeof getPathFromState = (state, options) =>
  stripInternalQueryParams(getPathFromState(state, options));

// Keyboard shortcut scopes used in sales portal stack.
// All scopes listed here are active by default. LOCATION_PICKER is intentionally
// excluded - it gets enabled dynamically when the picker opens (like operator portal).
const salesPortalKeyboardScope = [
  SalesPortalKeyboardScope.CHECKOUT_PANEL,
  SalesPortalKeyboardScope.GLOBAL,
  SalesPortalKeyboardScope.ORDER_ENTRY_TABLE,
  SalesPortalKeyboardScope.PRICING_MANAGEMENT,
];

// Tamagui's AlertDialog defaults to width: 'auto', which on native resolves
// to min-content and clips multi-line body copy. Pin a tablet-friendly floor.
const ALERT_MIN_WIDTH_NATIVE = 420;

interface SalesPortalNavigatorContentProps {
  navigationGuardStatus: NavigationGuardStatus;
  onSupplierRestaurantChange: (settings: SalesPortalUserSettings) => void;
  selectedRestaurantUUID: string;
  selectedSupplierUUID: string;
}

const SalesPortalNavigatorContent: React.FC<
  SalesPortalNavigatorContentProps
> = ({
  navigationGuardStatus,
  onSupplierRestaurantChange,
  selectedRestaurantUUID,
  selectedSupplierUUID,
}) => {
  // Read once at mount: initialRouteName is only honored on first render, and
  // a reactive read would re-render the navigator subtree on every tab switch
  // now that the persisted value changes per tab. Safe because mmkv hydration
  // is synchronous.
  const [initialLandingRoute] = useState(
    () => useSalesPortalNavigationStore.getState().lastActiveRoute,
  );
  const setLastActiveRoute = useSalesPortalNavigationStore(
    (state) => state.setLastActiveRoute,
  );
  const { appMinimumVersion } = useAppMinimumVersion();
  const defaultScreenOptions = useDefaultStackScreenOptions();
  const theme = useTheme();
  const { layoutSize } = useLayoutContext();
  const isMobileBottomNavLayout = !isWeb && layoutSize === LayoutSize.SMALL;

  // Disable slide transitions and swipe-to-navigate on native since sales portal
  // screen changes (e.g. restaurant selection) should feel like in-place updates,
  // not stack pushes. Web keeps defaults for browser back/forward support.
  const stackScreenOptions = useMemo(
    (): StackNavigationOptions => ({
      ...defaultScreenOptions,
      // `forNoAnimation` has to be explicit: the transition preset in the
      // defaults already sets a `cardStyleInterpolator`, so React Navigation
      // never falls back to it on its own and the card below keeps
      // `forHorizontalIOS`'s -30% translate.
      ...(isWeb
        ? {}
        : {
            animationEnabled: false,
            cardStyleInterpolator: CardStyleInterpolators.forNoAnimation,
            gestureEnabled: false,
          }),
    }),
    [defaultScreenOptions],
  );

  const detailScreenOptions: StackNavigationOptions | undefined =
    isMobileBottomNavLayout
      ? {
          animationEnabled: true,
          cardStyleInterpolator: defaultScreenOptions.cardStyleInterpolator,
          gestureEnabled: true,
        }
      : undefined;

  useSalesPortalFennelInit({
    restaurantUUID: selectedRestaurantUUID,
    supplierUUID: selectedSupplierUUID,
  });

  const isIntercomEnabled = Platform.OS === 'web';

  // LinkingOptions<T> strips nested `screens` from PathConfigMap — a known
  // React Navigation limitation. The nested config is valid at runtime and
  // validated when NavigationContainer mounts.
  const linking = useMemo(
    () => ({
      config: {
        screens: {
          [SALES_PORTAL_TAB_ROUTES.Accounts]: {
            screens: accountsTabScreens,
          },
          [SALES_PORTAL_TAB_ROUTES.Insights]: {
            screens: insightsTabScreens,
          },
          [SALES_PORTAL_TAB_ROUTES.OrderEntry]: {
            screens: orderEntryTabScreens,
          },
          [SALES_PORTAL_TAB_ROUTES.OrderGuides]: {
            screens: orderGuidesTabScreens,
          },
          [SALES_PORTAL_TAB_ROUTES.Pricing]: {
            screens: pricingTabScreens,
          },
          [SALES_PORTAL_TAB_ROUTES.Prospects]: {
            screens: prospectsTabScreens,
          },
          ...directTabScreens,
        },
      },
      enabled: true,
      getPathFromState: salesPortalGetPathFromState,
      prefixes: ['/'],
    }),
    [],
  );

  return (
    <AlertContainer
      {...(Platform.OS === 'web' ? {} : { minWidth: ALERT_MIN_WIDTH_NATIVE })}
    >
      <ToastProvider placement="top-right">
        <KeyboardShortcutsProvider scopes={salesPortalKeyboardScope}>
          <NavigationStackGuard
            appMinimumVersion={appMinimumVersion}
            navigationGuardStatus={navigationGuardStatus}
          >
            <NavigationContainer
              independent
              linking={linking as LinkingOptions<SalesPortalTabParams>}
              onStateChange={async (state) => {
                // Persist which tab is active for next visit
                const activeTabRoute = state?.routes?.[state?.index ?? 0];
                if (activeTabRoute?.name) {
                  setLastActiveRoute(toLandingRoute(activeTabRoute.name));
                }

                const activeRoute = getActiveRoute(state);
                const params = activeRoute?.params as
                  | { restaurantUUID?: string; supplierUUID?: string }
                  | undefined;

                // Persist selection whenever the active route has both IDs (Order Entry,
                // Account Detail). Keeps Fennel and other consumers in sync with the
                // current page and avoids stale supplier/restaurant when navigating away.
                if (params?.supplierUUID && params?.restaurantUUID) {
                  const settings = {
                    selectedRestaurantUUID: params.restaurantUUID,
                    selectedSupplierUUID: params.supplierUUID,
                  };
                  try {
                    const adapter =
                      new UserSettingsAdapter<SalesPortalUserSettings>(
                        USER_SETTINGS_KEY,
                      );
                    const savedUserSettings = await adapter.fetch();

                    await adapter.save({
                      ...(savedUserSettings || {}),
                      ...settings,
                    });
                  } catch (e) {}
                  onSupplierRestaurantChange(settings);
                }
              }}
              theme={{
                ...DefaultTheme,
                colors: {
                  ...DefaultTheme.colors,
                  background: theme.colors.white,
                  primary: theme.colors.primary,
                },
              }}
            >
              <Tab.Navigator
                backBehavior="history"
                initialRouteName={toLandingRoute(
                  initialLandingRoute ?? SALES_PORTAL_ROUTES.Home,
                )}
                screenOptions={{ headerShown: false, lazy: true }}
                // Mounted once, persistent across navigation; null outside
                // the phone-native layout (desktop chrome is the sidebar)
                tabBar={renderSalesPortalTabBar}
              >
                <Tab.Screen name={SALES_PORTAL_TAB_ROUTES.Accounts}>
                  {() => (
                    <AccountsStackNav.Navigator
                      initialRouteName={SALES_PORTAL_ROUTES.AccountList}
                      screenOptions={stackScreenOptions}
                    >
                      <AccountsStackNav.Screen
                        component={AccountListScreen}
                        name={SALES_PORTAL_ROUTES.AccountList}
                        options={{ title: i18n.t('accounts') }}
                      />
                      <AccountsStackNav.Screen
                        component={AccountDetailScreen}
                        initialParams={{
                          restaurantUUID: selectedRestaurantUUID,
                          supplierUUID: selectedSupplierUUID,
                        }}
                        name={SALES_PORTAL_ROUTES.AccountDetail}
                        options={detailScreenOptions}
                      />
                      <AccountsStackNav.Screen
                        component={EmployeeListScreen}
                        initialParams={{
                          restaurantUUID: selectedRestaurantUUID,
                          supplierUUID: selectedSupplierUUID,
                        }}
                        name={SALES_PORTAL_ROUTES.EmployeeList}
                        options={{
                          title: i18n.t('sales_portal.employee_list.title'),
                        }}
                      />
                    </AccountsStackNav.Navigator>
                  )}
                </Tab.Screen>
                <Tab.Screen
                  component={SuperintelligenceScreen}
                  name={SALES_PORTAL_ROUTES.Superintelligence}
                  options={{
                    title: i18n.t('sales_portal.superintelligence.title'),
                  }}
                />
                <Tab.Screen
                  component={CopilotChatScreen}
                  name={SALES_PORTAL_ROUTES.Copilot}
                  options={{
                    title: i18n.t('sales_portal.copilot.title'),
                  }}
                />
                <Tab.Screen name={SALES_PORTAL_TAB_ROUTES.Insights}>
                  {() => (
                    <InsightsStackNav.Navigator
                      initialRouteName={SALES_PORTAL_ROUTES.Insights}
                      screenOptions={stackScreenOptions}
                    >
                      <InsightsStackNav.Screen
                        name={SALES_PORTAL_ROUTES.Insights}
                        options={{ title: i18n.t('insights_hub') }}
                      >
                        {() => (
                          <InsightsScreenWrapper
                            supplierUUID={selectedSupplierUUID}
                          />
                        )}
                      </InsightsStackNav.Screen>
                      <InsightsStackNav.Screen
                        component={AccountDetailScreen}
                        name={SALES_PORTAL_ROUTES.AccountDetail}
                        options={detailScreenOptions}
                      />
                      <InsightsStackNav.Screen
                        component={EmployeeListScreen}
                        name={SALES_PORTAL_ROUTES.EmployeeList}
                        options={{
                          title: i18n.t('sales_portal.employee_list.title'),
                        }}
                      />
                    </InsightsStackNav.Navigator>
                  )}
                </Tab.Screen>
                <Tab.Screen name={SALES_PORTAL_TAB_ROUTES.OrderEntry}>
                  {() => (
                    <OrderEntryStackNav.Navigator
                      initialRouteName={SALES_PORTAL_ROUTES.OrderEntry}
                      screenOptions={stackScreenOptions}
                    >
                      <OrderEntryStackNav.Screen
                        component={OrderEntryScreenWrapper}
                        initialParams={{
                          restaurantUUID: selectedRestaurantUUID,
                          supplierUUID: selectedSupplierUUID,
                        }}
                        name={SALES_PORTAL_ROUTES.OrderEntry}
                        options={{
                          title: i18n.t('sales_portal.order_entry'),
                        }}
                      />
                      <OrderEntryStackNav.Screen
                        component={SalesPortalNotFoundScreen}
                        name={SALES_PORTAL_ROUTES.NotFound}
                        options={{
                          title: i18n.t('sales_portal.not_found.title'),
                        }}
                      />
                    </OrderEntryStackNav.Navigator>
                  )}
                </Tab.Screen>
                <Tab.Screen name={SALES_PORTAL_TAB_ROUTES.OrderGuides}>
                  {() => (
                    <OrderGuideStackNav.Navigator
                      initialRouteName={SALES_PORTAL_ROUTES.OrderGuides}
                      screenOptions={stackScreenOptions}
                    >
                      <OrderGuideStackNav.Screen
                        component={OrderGuidesListScreen}
                        initialParams={{
                          restaurantUUID: selectedRestaurantUUID,
                          supplierUUID: selectedSupplierUUID,
                        }}
                        name={SALES_PORTAL_ROUTES.OrderGuides}
                        options={{
                          title: i18n.t(
                            'sales_portal.order_guides_list.page_title',
                          ),
                        }}
                      />
                    </OrderGuideStackNav.Navigator>
                  )}
                </Tab.Screen>
                <Tab.Screen
                  component={PriceQuotesScreen}
                  name={SALES_PORTAL_ROUTES.PriceQuotesBuilder}
                  options={{
                    title: i18n.t('sales_portal.price_quotes.title'),
                  }}
                />
                <Tab.Screen name={SALES_PORTAL_TAB_ROUTES.Prospects}>
                  {() => (
                    <ProspectsStackNav.Navigator
                      initialRouteName={SALES_PORTAL_ROUTES.ProspectFinder}
                      screenOptions={stackScreenOptions}
                    >
                      <ProspectsStackNav.Screen
                        component={ProspectFinderScreen}
                        initialParams={{
                          supplierUUID: selectedSupplierUUID,
                        }}
                        name={SALES_PORTAL_ROUTES.ProspectFinder}
                        options={{
                          title: i18n.t('sales_portal.sidebar.prospects'),
                        }}
                      />
                      <ProspectsStackNav.Screen
                        component={ProspectsScreen}
                        initialParams={{
                          supplierUUID: selectedSupplierUUID,
                        }}
                        name={SALES_PORTAL_ROUTES.Prospects}
                        options={{
                          title: i18n.t('sales_portal.sidebar.prospects'),
                        }}
                      />
                      <ProspectsStackNav.Screen
                        component={ProspectDetailScreen}
                        initialParams={{
                          restaurantUUID: selectedRestaurantUUID,
                          supplierUUID: selectedSupplierUUID,
                        }}
                        name={SALES_PORTAL_ROUTES.ProspectDetail}
                        options={{
                          ...detailScreenOptions,
                          title: i18n.t('sales_portal.prospect_detail.title'),
                        }}
                      />
                      <ProspectsStackNav.Screen
                        component={EmployeeListScreen}
                        initialParams={{
                          restaurantUUID: selectedRestaurantUUID,
                          supplierUUID: selectedSupplierUUID,
                        }}
                        name={SALES_PORTAL_ROUTES.ProspectEmployeeList}
                        options={{
                          title: i18n.t('sales_portal.employee_list.title'),
                        }}
                      />
                    </ProspectsStackNav.Navigator>
                  )}
                </Tab.Screen>
                <Tab.Screen name={SALES_PORTAL_TAB_ROUTES.Pricing}>
                  {() => (
                    <PricingStackNav.Navigator
                      initialRouteName={SALES_PORTAL_ROUTES.PricingOverview}
                      screenOptions={stackScreenOptions}
                    >
                      <PricingStackNav.Screen
                        component={PricingOverviewScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingOverview}
                        options={{ title: i18n.t('pricing.overview.title') }}
                      />
                      <PricingStackNav.Screen
                        component={PricingManagementScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingManagement}
                        options={{ title: i18n.t('pricing.title') }}
                      />
                      <PricingStackNav.Screen
                        component={PricingItemManagementScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingItemManagement}
                        // Keep the cost-changes list mounted underneath so
                        // returning via the breadcrumb reads from cache instead
                        // of remounting and re-fetching.
                        options={{
                          detachPreviousScreen: false,
                          title: i18n.t('pricing.title'),
                        }}
                      />
                      <PricingStackNav.Screen
                        component={PricingManagementReviewScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingManagementReview}
                        options={{ title: i18n.t('pricing.title') }}
                      />
                      <PricingStackNav.Screen
                        component={PricingCustomersScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingCustomers}
                        options={{ title: i18n.t('pricing.title') }}
                      />
                      <PricingStackNav.Screen
                        component={PricingCustomerDetailScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingCustomerDetail}
                        options={{ title: i18n.t('pricing.title') }}
                      />
                      <PricingStackNav.Screen
                        component={PricingAgentTasksScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingAgentTasks}
                        options={{ title: i18n.t('pricing.agent.title') }}
                      />
                      <PricingStackNav.Screen
                        component={PricingAgentReviewScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingAgentReview}
                        options={{ title: i18n.t('pricing.agent.title') }}
                      />
                      <PricingStackNav.Screen
                        component={PricingBookmarksScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingBookmarked}
                        options={{
                          title: i18n.t('pricing.bookmarks.sidenav_title'),
                        }}
                      />
                      <PricingStackNav.Screen
                        component={PricingMicroProductScreenWrapper}
                        name={SALES_PORTAL_ROUTES.PricingMicroProduct}
                        options={{ title: i18n.t('pricing.title') }}
                      />
                      <PricingStackNav.Screen
                        component={SalesPortalNotFoundScreen}
                        name={SALES_PORTAL_ROUTES.NotFound}
                        options={{
                          title: i18n.t('sales_portal.not_found.title'),
                        }}
                      />
                    </PricingStackNav.Navigator>
                  )}
                </Tab.Screen>
                <Tab.Screen
                  component={SalesPortalHomeScreen}
                  name={SALES_PORTAL_ROUTES.Home}
                  options={{ title: i18n.t('sales_portal.home.title') }}
                />
                <Tab.Screen
                  component={MicroProductLoaderScreen}
                  name={SALES_PORTAL_ROUTES.Base}
                  options={{ title: i18n.t('sales_portal.home.title') }}
                />
              </Tab.Navigator>
              {/* because of the nested contexts we need to mount this component to
               record the route changes */}
              {isIntercomEnabled ? <IntercomRouteSync /> : null}
            </NavigationContainer>
          </NavigationStackGuard>
        </KeyboardShortcutsProvider>
      </ToastProvider>
    </AlertContainer>
  );
};

export const SalesPortalStack: React.FC = () => {
  const navigationGuardStatus = useNavigationGuardStatus();
  const { switchToExperience } = useExperienceStore();

  // Prevents redirecting to operator portal multiple times
  const isRedirecting = useRef(false);

  const [userSettings, setUserSettings] = useState<
    SalesPortalUserSettings | UserSettingsStatus
  >(UserSettingsStatus.LOADING);

  // one time fetch of user settings to get selected restaurant from operator experience
  useEffect(() => {
    const initializeSettings = async () => {
      const adapter = new UserSettingsAdapter<SalesPortalUserSettings>(
        USER_SETTINGS_KEY,
      );

      // 1. Only check URL for params on web initial page load
      if (isWeb) {
        const initialUrl = await Linking.getInitialURL();
        const currentPath =
          typeof window !== 'undefined' ? window.location.pathname : null;
        const initialPath = initialUrl ? new URL(initialUrl).pathname : null;
        const isInitialLoad = initialPath === currentPath;

        if (isInitialLoad && initialPath) {
          try {
            const state = getStateFromPath(
              initialPath,
              initialUrlLinkingConfig,
            );
            // ResultState from getStateFromPath has stale: true | undefined
            // vs NavigationState's stale: false — structurally compatible
            // but TS can't reconcile the literal types without unknown.
            const activeRoute = state
              ? getActiveRoute(state as unknown as NavigationState)
              : undefined;
            const params = activeRoute?.params as
              | Partial<SalesPortalOrderEntryParams>
              | undefined;

            if (params?.supplierUUID && params?.restaurantUUID) {
              const settings = {
                selectedRestaurantUUID: params.restaurantUUID,
                selectedSupplierUUID: params.supplierUUID,
              };
              await adapter.save(settings);
              setUserSettings(settings);
              return;
            }
          } catch {
            // Invalid URL - fall through to check saved settings
          }
        }
      }

      // 2. No URL params (or not initial load) - check saved settings
      const savedSettings = await adapter.fetch();
      if (
        savedSettings?.selectedRestaurantUUID &&
        savedSettings?.selectedSupplierUUID
      ) {
        setUserSettings(savedSettings);
        return;
      }

      // 3. No settings found
      setUserSettings(UserSettingsStatus.NOT_FOUND);
    };

    void initializeSettings();
  }, []);

  // Show loading while auth or settings are being determined
  if (!navigationGuardStatus || userSettings === UserSettingsStatus.LOADING) {
    return (
      <ToastProvider placement="top-right">
        <LogoLoadingIndicator />
      </ToastProvider>
    );
  }

  // No settings found - redirect to operator portal
  if (userSettings === UserSettingsStatus.NOT_FOUND) {
    if (!isRedirecting.current) {
      isRedirecting.current = true;
      switchToExperience(ExperienceMode.OPERATOR_PORTAL);
      if (isWeb) {
        window.location.replace('/');
      }
    }
    return null;
  }

  return (
    <SalesPortalIntercomProvider
      navigationGuardStatus={navigationGuardStatus}
      restaurantUUID={userSettings.selectedRestaurantUUID}
      supplierUUID={userSettings.selectedSupplierUUID}
    >
      <SalesPortalNavigatorContent
        navigationGuardStatus={navigationGuardStatus}
        // Update state when route has supplier/restaurant so Fennel stays in sync without reload.
        onSupplierRestaurantChange={setUserSettings}
        selectedRestaurantUUID={userSettings.selectedRestaurantUUID}
        selectedSupplierUUID={userSettings.selectedSupplierUUID}
      />
    </SalesPortalIntercomProvider>
  );
};
